import React, { useContext, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import LogoSVG from '../components/ui/LogoSVG';
import './Home.css';

const Home: React.FC = () => {
  const authContext = useContext(AuthContext);
  
  if (!authContext) {
    throw new Error('Home must be used within an AuthProvider');
  }
  
  const { currentUser } = authContext;

  // If user is already logged in, redirect to dashboard
  useEffect(() => {
    if (currentUser) {
      window.location.href = '/dashboard';
    }
  }, [currentUser]);

  return (
    <div className="home-page dark-theme">
      <div className="animated-background">
        <div className="gradient-orb orb1"></div>
        <div className="gradient-orb orb2"></div>
        <div className="gradient-orb orb3"></div>
      </div>
      
      <header className="home-header">
        <div className="header-content">
          <h1 className="logo reveal-text"><LogoSVG width={250} height={60} className="glow-text" /></h1>
          <div className="auth-buttons">
            <Link to="/login" className="btn btn-primary pulse-effect">Login</Link>
          </div>
        </div>
      </header>

      <main className="home-main">
        <section className="hero-section">
          <div className="container">
            <div className="hero-content slide-in-left">
              <h2 className="glow-text">Elevate Your Debate Experience</h2>
              <div className="hero-text-container">
                <p className="hero-description fade-in-text">
                  A comprehensive platform for monitoring attendance, allocating
                  debates, and tracking performance in parliamentary debates.
                </p>
              </div>
              <Link to="/login" className="btn btn-primary btn-lg">Get Started</Link>
            </div>
            <div className="hero-image slide-in-right">
              <div className="debate-illustration">
                <div className="illustration-element podium"></div>
                <div className="illustration-element debater debater-1"></div>
                <div className="illustration-element debater debater-2"></div>
                <div className="illustration-element judge"></div>
              </div>
            </div>
          </div>
        </section>

        <section className="features-section">
          <div className="container">
            <h2 className="section-title reveal-text">Key Features</h2>
            <div className="features-grid">
              <div className="feature-card float-up" style={{animationDelay: '0s'}}>
                <div className="feature-icon">📊</div>
                <h3>Performance Tracking</h3>
                <p>Track your debate performance with detailed statistics and analytics.</p>
              </div>
              
              <div className="feature-card float-up" style={{animationDelay: '0.2s'}}>
                <div className="feature-icon">🔄</div>
                <h3>Round Management</h3>
                <p>Easily create and manage debate rounds with customizable formats.</p>
              </div>
              
              <div className="feature-card float-up" style={{animationDelay: '0.4s'}}>
                <div className="feature-icon">👥</div>
                <h3>Role Allocation</h3>
                <p>Efficiently allocate roles for debaters and adjudicators.</p>
              </div>
              
              <div className="feature-card float-up" style={{animationDelay: '0.6s'}}>
                <div className="feature-icon">📝</div>
                <h3>Motion Library</h3>
                <p>Access a growing library of debate motions across various themes.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="cta-section parallax-effect">
          <div className="container">
            <div className="cta-content slide-up">
              <h2 className="glow-text">Ready to enhance your debate skills?</h2>
              <p>Join our community of debaters and adjudicators today.</p>
              <Link to="/login" className="btn btn-primary btn-lg">Login Now</Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="home-footer">
        <div className="container">
          <p>&copy; {new Date().getFullYear()} The League System</p>
          <p>Created by Caraga State University</p>
        </div>
      </footer>
    </div>
  );
};

export default Home; 